import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SignaturePad from "react-signature-canvas";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import PauseIcon from "@mui/icons-material/Pause";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { useSearchParams } from "react-router";
import {
  HIPAA_TRAINING_ASSET_IDS,
  HIPAA_TRAINING_COPY,
  HIPAA_TRAINING_QUESTIONS,
  HIPAA_TRAINING_STATUSES,
  type HipaaAnswerOption,
  type HipaaTrainingStatus,
} from "../config/hipaaTrainingConfig";
import { getSignatureDataUrl, isSignatureValid } from "../utils/signatureValidation";
import NavStatusBar from "../components/NavStatusBar";

type QuestionFieldName = (typeof HIPAA_TRAINING_QUESTIONS)[number]["fieldName"];

interface HipaaTrainingRecord {
  id: number;
  nexusId: string;
  userNexusId: string | null;
  email?: string | null;
  status: HipaaTrainingStatus | string;
  completedAt: string | null;
  traineeName: string | null;
  retriesUsed: number;
  timesRestarted: number;
  question1Answer: HipaaAnswerOption | "" | null;
  question2Answer: HipaaAnswerOption | "" | null;
  question3Answer: HipaaAnswerOption | "" | null;
  question4Answer: HipaaAnswerOption | "" | null;
  question5Answer: HipaaAnswerOption | "" | null;
  correctAnswerCount: number;
  score: number;
  signature?: string | null;
  videoCheckpointSeconds: number;
  videoCompleted: boolean;
  stepMetadata?: Record<string, unknown> | null;
}

interface SessionResponse {
  access: "ok" | "completed" | "verification_required" | "email_required";
  isExisting?: boolean;
  message?: string;
  emailHint?: string;
  user: {
    id: string;
    name: string;
  };
  training?: HipaaTrainingRecord;
}

interface VerifyEmailResponse {
  success: boolean;
  token?: string;
  user?: {
    id: string;
    name: string;
  };
  verificationRequired?: boolean;
  challengeToken?: string;
  message?: string;
}

type MessageState = {
  severity: "success" | "info" | "warning" | "error";
  text: string;
} | null;

type QuestionFeedbackState = {
  answer: HipaaAnswerOption;
  incorrectAnswers?: HipaaAnswerOption[];
  isCorrect?: boolean;
  severity: "success" | "warning" | "error";
  text: string;
  action: "retry" | "advance" | "restart";
  nextStatus?: HipaaTrainingStatus;
  finalScore?: number;
} | null;

type StepMetadataShape = {
  questionFeedbackByStatus?: Partial<Record<HipaaTrainingStatus, QuestionFeedbackState>>;
};

const MAX_RETRIES = 3;
const API_BASE_URL = (import.meta.env.VITE_API_URL || "/api").replace(/\/$/, "");
const ASSET_BASE_URL = `${API_BASE_URL}/hipaa-training/assets`;
const HIPAA_ACCESS_TOKEN_STORAGE_PREFIX = "hipaa-training-access:";
const VIDEO_CONTROLS_HIDE_DELAY_MS = 2200;

function formatDateTime(value: string | null | undefined) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed);
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.message || `Request failed (${response.status})`) as Error & {
      response?: { status: number; data: any };
    };
    error.response = { status: response.status, data };
    throw error;
  }
  return data as T;
}

async function apiGet<T>(url: string, options: { params?: Record<string, string>; headers?: HeadersInit } = {}): Promise<T> {
  const nextUrl = new URL(url, window.location.origin);
  Object.entries(options.params || {}).forEach(([key, value]) => {
    nextUrl.searchParams.set(key, value);
  });
  const response = await fetch(nextUrl.toString(), {
    method: "GET",
    headers: options.headers,
  });
  return parseJsonResponse<T>(response);
}

async function apiPost<T>(url: string, body: unknown, options: { headers?: HeadersInit } = {}): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    body: JSON.stringify(body),
  });
  return parseJsonResponse<T>(response);
}

function formatDuration(totalSeconds: number) {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds || 0));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function normalizeStepMetadata(stepMetadata: HipaaTrainingRecord["stepMetadata"]): StepMetadataShape {
  if (!stepMetadata || typeof stepMetadata !== "object" || Array.isArray(stepMetadata)) {
    return {};
  }

  return stepMetadata as StepMetadataShape;
}

type FullscreenCapableElement = HTMLDivElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

type FullscreenCapableDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

export default function HIPAATraining() {
  const [searchParams] = useSearchParams();
  const userId = (searchParams.get("id") || "").trim();
  const isEmailMode =
    (searchParams.get("email") || searchParams.get("emailMode") || "").trim().toLowerCase() === "true";
  const accessTokenStorageKey = userId ? `${HIPAA_ACCESS_TOKEN_STORAGE_PREFIX}${userId}` : null;

  const [session, setSession] = useState<HipaaTrainingRecord | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [accessState, setAccessState] = useState<
    "loading" | "verification_required" | "email_required" | "ok" | "invalid" | "completed"
  >("loading");
  const [accessToken, setAccessToken] = useState("");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [verificationName, setVerificationName] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [emailChallengeToken, setEmailChallengeToken] = useState("");
  const [verificationMessage, setVerificationMessage] = useState("");
  const [verificationEmailHint, setVerificationEmailHint] = useState("");
  const [verificationError, setVerificationError] = useState("");
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [pageMessage, setPageMessage] = useState<MessageState>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloadingCertificate, setIsDownloadingCertificate] = useState(false);
  const [certificateError, setCertificateError] = useState("");
  const [completedViewMode, setCompletedViewMode] = useState<"existing" | "fresh" | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<HipaaAnswerOption | "">("");
  const [answerError, setAnswerError] = useState("");
  const [questionFeedback, setQuestionFeedback] = useState<QuestionFeedbackState>(null);
  const [videoError, setVideoError] = useState("");
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentPlaybackSeconds, setCurrentPlaybackSeconds] = useState(0);
  const [watchedProgressSeconds, setWatchedProgressSeconds] = useState(0);
  const [seekPreviewSeconds, setSeekPreviewSeconds] = useState<number | null>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [areVideoControlsVisible, setAreVideoControlsVisible] = useState(true);
  const [isAttested, setIsAttested] = useState(false);
  const [signatureData, setSignatureData] = useState("");
  const [signatureRefreshKey, setSignatureRefreshKey] = useState(0);
  const [attestationError, setAttestationError] = useState("");

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoFrameRef = useRef<HTMLDivElement | null>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const lastHydratedSignatureRef = useRef("");
  const maxAllowedTimeRef = useRef(0);
  const lastSavedCheckpointRef = useRef(0);
  const isProgrammaticSeekRef = useRef(false);
  const videoControlsHideTimeoutRef = useRef<number | null>(null);

  const assetTokenQuery = accessToken ? `?token=${encodeURIComponent(accessToken)}` : "";
  const overviewImageUrl =
    `${ASSET_BASE_URL}/${HIPAA_TRAINING_ASSET_IDS.overviewImage}${assetTokenQuery}`;
  const thankYouImageUrl =
    `${ASSET_BASE_URL}/${HIPAA_TRAINING_ASSET_IDS.thankYouImage}${assetTokenQuery}`;
  const trainingVideoUrl =
    `${ASSET_BASE_URL}/${HIPAA_TRAINING_ASSET_IDS.trainingVideo}${assetTokenQuery}`;

  const currentStatus = useMemo<HipaaTrainingStatus>(() => {
    const status = String(session?.status || HIPAA_TRAINING_STATUSES[0]);
    const matched = HIPAA_TRAINING_STATUSES.find((value) => value === status);
    return matched || HIPAA_TRAINING_STATUSES[0];
  }, [session?.status]);

  const currentQuestion = useMemo(
    () => HIPAA_TRAINING_QUESTIONS.find((question) => question.status === currentStatus) || null,
    [currentStatus]
  );
  const currentQuestionPersistedFeedback = useMemo(() => {
    if (!currentQuestion || !session) {
      return null;
    }

    const metadata = normalizeStepMetadata(session.stepMetadata);
    return metadata.questionFeedbackByStatus?.[currentQuestion.status] || null;
  }, [currentQuestion, session]);

  const currentStepIndex = Math.max(0, HIPAA_TRAINING_STATUSES.indexOf(currentStatus));
  const retriesRemaining = Math.max(0, MAX_RETRIES - (session?.retriesUsed || 0));
  const seekableProgressSeconds = session?.videoCompleted
    ? Math.max(0, Math.floor(videoDuration || watchedProgressSeconds))
    : Math.max(
        0,
        Math.floor(
          Math.max(
            watchedProgressSeconds,
            session?.videoCheckpointSeconds || 0,
            maxAllowedTimeRef.current
          )
        )
      );
  const seekableProgressPercent =
    videoDuration > 0 ? Math.min(100, (seekableProgressSeconds / videoDuration) * 100) : 0;
  const displayedSeekSeconds = seekPreviewSeconds ?? currentPlaybackSeconds;
  const displayedSeekPercent =
    videoDuration > 0 ? Math.min(100, (displayedSeekSeconds / videoDuration) * 100) : 0;
  const showScoreFooter =
    currentStepIndex >= HIPAA_TRAINING_STATUSES.indexOf("Question 1");
  const isQuestionLocked = Boolean(questionFeedback && questionFeedback.action !== "retry");
  const incorrectAnswers = questionFeedback?.incorrectAnswers || [];
  const retryDisabledAnswers =
    questionFeedback?.action === "retry" ? incorrectAnswers : [];
  const displayedSelectedAnswer = isQuestionLocked
    ? (questionFeedback?.answer || selectedAnswer)
    : selectedAnswer;
  const furthestAccessibleStepIndex = useMemo(() => {
    if (!session) {
      return currentStepIndex;
    }

    let furthestIndex = currentStepIndex;

    if (session.videoCompleted) {
      furthestIndex = Math.max(furthestIndex, HIPAA_TRAINING_STATUSES.indexOf("Video"));
    }

    HIPAA_TRAINING_QUESTIONS.forEach((question) => {
      if (String(session[question.fieldName] || "").trim()) {
        furthestIndex = Math.max(
          furthestIndex,
          HIPAA_TRAINING_STATUSES.indexOf(question.status)
        );
      }
    });

    const persistedQuestionFeedbackByStatus =
      normalizeStepMetadata(session.stepMetadata).questionFeedbackByStatus || {};

    Object.keys(persistedQuestionFeedbackByStatus).forEach((status) => {
      furthestIndex = Math.max(
        furthestIndex,
        HIPAA_TRAINING_STATUSES.indexOf(status as HipaaTrainingStatus)
      );
    });

    const answeredAllQuestions = HIPAA_TRAINING_QUESTIONS.every((question) =>
      Boolean(String(session[question.fieldName] || "").trim())
    );

    if (
      currentStatus === "Attestation" ||
      currentStatus === "Completed" ||
      (answeredAllQuestions && Number(session.correctAnswerCount || 0) >= 4)
    ) {
      furthestIndex = Math.max(
        furthestIndex,
        HIPAA_TRAINING_STATUSES.indexOf("Attestation")
      );
    }

    return furthestIndex;
  }, [currentStatus, currentStepIndex, session]);
  const statusBarOptions = useMemo(
    () =>
      HIPAA_TRAINING_STATUSES.map((status, index) => ({
        value: status,
        label: status,
        disabled: status === "Completed" || index > furthestAccessibleStepIndex,
      })),
    [furthestAccessibleStepIndex]
  );
  const shouldShowVideoControls = !isVideoPlaying || areVideoControlsVisible;
  const previousStatus =
    currentStepIndex > 0
      ? (HIPAA_TRAINING_STATUSES[currentStepIndex - 1] as HipaaTrainingStatus)
      : null;

  const loadSession = useCallback(
    async (tokenOverride?: string) => {
      const nextAccessToken =
        tokenOverride ??
        (accessTokenStorageKey ? sessionStorage.getItem(accessTokenStorageKey) ?? "" : "");

      try {
        const responseData = await apiGet<SessionResponse>(`${API_BASE_URL}/hipaa-training/session`, {
          params: userId ? { id: userId } : { emailMode: "true" },
          headers: nextAccessToken
            ? { Authorization: `Bearer ${nextAccessToken}` }
            : undefined,
        });

        setDisplayName(responseData.user?.name || "");
        setVerificationEmailHint(responseData.emailHint || "");

        if (
          responseData.access === "verification_required" ||
          responseData.access === "email_required"
        ) {
          setAccessToken("");
          if (accessTokenStorageKey) sessionStorage.removeItem(accessTokenStorageKey);
          setSession(null);
          setAccessState(responseData.access);
          setPageMessage(null);
          return;
        }

        if (nextAccessToken) {
          setAccessToken(nextAccessToken);
        }

        if (!responseData.training) {
          setAccessState("invalid");
          setPageMessage({
            severity: "error",
            text: "Unable to load this training session.",
          });
          return;
        }

        const training = responseData.training;
        setVerificationError("");
        setSession(training);
        setSignatureData(training.signature || "");
        setCurrentPlaybackSeconds(
          Math.max(0, Math.floor(training.videoCheckpointSeconds || 0))
        );
        setWatchedProgressSeconds(
          Math.max(0, Math.floor(training.videoCheckpointSeconds || 0))
        );
        maxAllowedTimeRef.current = Math.max(
          0,
          Math.floor(training.videoCheckpointSeconds || 0)
        );
        lastSavedCheckpointRef.current = Math.max(
          0,
          Math.floor(training.videoCheckpointSeconds || 0)
        );

        if (responseData.access === "completed") {
          setAccessState("completed");
          setCompletedViewMode("existing");
          setPageMessage({
            severity: "info",
            text:
              responseData.message ||
              "This HIPAA training has already been completed for this user.",
          });
        } else {
          setAccessState("ok");
        }
      } catch (error: any) {
        const status = error?.response?.status;
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "This training link is not valid.";

        if (status === 401) {
          setAccessToken("");
          if (accessTokenStorageKey) sessionStorage.removeItem(accessTokenStorageKey);
          setSession(null);
          setAccessState("verification_required");
          setVerificationError(message);
          return;
        }

        setAccessState("invalid");
        setPageMessage({ severity: "error", text: message });
      }
    },
    [accessTokenStorageKey, isEmailMode, userId]
  );

  useEffect(() => {
    if (!userId && !isEmailMode) {
      setAccessState("invalid");
      setPageMessage({
        severity: "error",
        text: "This training link is not valid.",
      });
      setIsInitialLoading(false);
      return;
    }

    let isMounted = true;

    void loadSession().finally(() => {
      if (isMounted) {
        setIsInitialLoading(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [isEmailMode, loadSession, userId]);

  useEffect(() => {
    const fullscreenDocument = document as FullscreenCapableDocument;
    const updateFullscreenState = () => {
      const fullscreenElement =
        document.fullscreenElement || fullscreenDocument.webkitFullscreenElement || null;
      setIsFullscreen(fullscreenElement === videoFrameRef.current);
    };

    document.addEventListener("fullscreenchange", updateFullscreenState);
    document.addEventListener("webkitfullscreenchange", updateFullscreenState as EventListener);

    return () => {
      document.removeEventListener("fullscreenchange", updateFullscreenState);
      document.removeEventListener(
        "webkitfullscreenchange",
        updateFullscreenState as EventListener
      );
    };
  }, []);

  useEffect(() => {
    if (!currentQuestion || !session) {
      setSelectedAnswer("");
      setQuestionFeedback(null);
      return;
    }

    const persistedAnswer =
      currentQuestionPersistedFeedback?.action === "retry"
        ? ""
        : currentQuestionPersistedFeedback?.answer || session[currentQuestion.fieldName];
    setSelectedAnswer((persistedAnswer || "") as HipaaAnswerOption | "");
    setAnswerError("");
    setQuestionFeedback(currentQuestionPersistedFeedback);
  }, [currentQuestion?.status, currentQuestionPersistedFeedback, session]);

  useEffect(() => {
    if (currentStatus !== "Attestation") {
      setIsAttested(false);
      setAttestationError("");
    }
  }, [currentStatus]);

  useEffect(() => {
    if (currentStatus !== "Attestation" || !signaturePadRef.current) {
      return;
    }

    const signatureToRender = String(session?.signature || signatureData || "").trim();

    if (signatureToRender) {
      if (lastHydratedSignatureRef.current === signatureToRender) {
        return;
      }

      try {
        signaturePadRef.current.clear();
        signaturePadRef.current.fromDataURL(signatureToRender);
        lastHydratedSignatureRef.current = signatureToRender;
      } catch (_error) {
        // Ignore redraw failures; users can always sign again.
      }
      return;
    }

    signaturePadRef.current.clear();
    lastHydratedSignatureRef.current = "";
  }, [currentStatus, session?.signature, signatureRefreshKey]);

  const handleVerifyEmail = async () => {
    const normalizedEmail = verificationEmail.trim();
    const normalizedName = verificationName.replace(/\s+/g, " ").trim();
    if (isEmailMode && normalizedName.length < 2) {
      setVerificationError("Enter your full name for the training certificate.");
      return;
    }
    if (!normalizedEmail) {
      setVerificationError("Enter the email address on your account to continue.");
      return;
    }
    if (isEmailMode && emailChallengeToken && !/^\d{6}$/.test(verificationCode.trim())) {
      setVerificationError("Enter the 6-digit verification code from your email.");
      return;
    }

    setIsVerifyingEmail(true);
    setVerificationError("");

    try {
      const responseData = await apiPost<VerifyEmailResponse>(
        `${API_BASE_URL}/hipaa-training/verify-email`,
        isEmailMode
          ? {
              email: normalizedEmail,
              traineeName: normalizedName,
              emailMode: true,
              ...(emailChallengeToken
                ? {
                    verificationCode: verificationCode.trim(),
                    challengeToken: emailChallengeToken,
                  }
                : {}),
            }
          : {
              id: userId,
              email: normalizedEmail,
            }
      );

      if (responseData.verificationRequired && responseData.challengeToken) {
        setEmailChallengeToken(responseData.challengeToken);
        setVerificationCode("");
        setVerificationMessage(
          responseData.message || "A verification code was sent to your email address."
        );
        return;
      }
      if (!responseData.token) {
        throw new Error("Unable to verify your email right now.");
      }
      if (accessTokenStorageKey) sessionStorage.setItem(accessTokenStorageKey, responseData.token);
      setAccessToken(responseData.token);
      setDisplayName(responseData.user?.name || "");
      await loadSession(responseData.token);
    } catch (error: any) {
      setVerificationError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to verify your email right now."
      );
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  const saveProgress = useCallback(
    async (
      updates: Record<string, unknown>,
      options: { silent?: boolean } = {}
    ) => {
      if (!session) {
        return null;
      }

      if (!options.silent) {
        setIsSaving(true);
      }

      try {
        const responseData = await apiPost<{ success: boolean; training: HipaaTrainingRecord }>(
          `${API_BASE_URL}/hipaa-training/progress`,
          {
            trainingId: session.id,
            id: userId,
            emailMode: isEmailMode,
            token: accessToken,
            formData: updates,
          },
          {
            headers: accessToken
              ? { Authorization: `Bearer ${accessToken}` }
              : undefined,
          }
        );

        setSession(responseData.training);
        maxAllowedTimeRef.current = Math.max(
          maxAllowedTimeRef.current,
          Math.floor(responseData.training.videoCheckpointSeconds || 0)
        );
        lastSavedCheckpointRef.current = Math.max(
          lastSavedCheckpointRef.current,
          Math.floor(responseData.training.videoCheckpointSeconds || 0)
        );

        return responseData.training;
      } catch (error: any) {
        if (error?.response?.status === 401) {
          setAccessToken("");
          if (accessTokenStorageKey) sessionStorage.removeItem(accessTokenStorageKey);
          setSession(null);
          setAccessState(isEmailMode ? "email_required" : "verification_required");
          setVerificationError(
            error?.response?.data?.message ||
              "Please enter your email address to continue."
          );
        }
        if (!options.silent) {
          throw error;
        }
        return null;
      } finally {
        if (!options.silent) {
          setIsSaving(false);
        }
      }
    },
    [accessToken, accessTokenStorageKey, isEmailMode, session, userId]
  );

  const handleStatusTransition = useCallback(
    async (nextStatus: HipaaTrainingStatus) => {
      if (currentStatus === "Video" && nextStatus !== "Video" && videoRef.current) {
        videoRef.current.pause();
        setIsVideoPlaying(false);
      }
      try {
        await saveProgress({ status: nextStatus });
        setPageMessage(null);
        setAnswerError("");
        setQuestionFeedback(null);
      } catch (error: any) {
        setPageMessage({
          severity: "error",
          text:
            error?.response?.data?.message ||
            error?.message ||
            "Unable to save your training progress right now.",
        });
      }
    },
    [currentStatus, saveProgress]
  );

  const buildQuestionFeedbackMetadata = useCallback(
    (
      questionStatus: HipaaTrainingStatus,
      nextFeedback: QuestionFeedbackState
    ): StepMetadataShape | null => {
      const metadata = normalizeStepMetadata(session?.stepMetadata ?? null);
      const nextQuestionFeedbackByStatus = {
        ...(metadata.questionFeedbackByStatus || {}),
      };

      if (nextFeedback) {
        nextQuestionFeedbackByStatus[questionStatus] = nextFeedback;
      } else {
        delete nextQuestionFeedbackByStatus[questionStatus];
      }

      const normalizedEntries = Object.entries(nextQuestionFeedbackByStatus).filter(
        ([, value]) => Boolean(value)
      );

      if (normalizedEntries.length === 0) {
        return null;
      }

      return {
        ...metadata,
        questionFeedbackByStatus: Object.fromEntries(normalizedEntries) as Partial<
          Record<HipaaTrainingStatus, QuestionFeedbackState>
        >,
      };
    },
    [session?.stepMetadata]
  );

  const resetTrainingAfterFailedQuiz = useCallback(
    async (finalScore: number) => {
      try {
        await saveProgress({ restartTraining: true });

        maxAllowedTimeRef.current = 0;
        lastSavedCheckpointRef.current = 0;
        setSelectedAnswer("");
        setAnswerError("");
        setQuestionFeedback(null);
        setVideoError("");
        setPageMessage({
          severity: "warning",
          text:
            `You scored ${finalScore} out of 5. A score of at least 4 is required, ` +
            "so the training has been reset and the video must be watched again.",
        });
      } catch (error: any) {
        setPageMessage({
          severity: "error",
          text:
            error?.response?.data?.message ||
            error?.message ||
            "Unable to reset the training after the quiz.",
        });
      }
    },
    [saveProgress]
  );

  const persistVideoCheckpoint = useCallback(
    async (checkpointSeconds: number, extraUpdates: Partial<HipaaTrainingRecord> = {}) => {
      const normalizedCheckpoint = Math.max(0, Math.floor(checkpointSeconds));

      try {
        await saveProgress(
          {
            videoCheckpointSeconds: normalizedCheckpoint,
            ...extraUpdates,
          },
          { silent: true }
        );
      } catch (error) {
        console.error("Error saving HIPAA video checkpoint:", error);
      }
    },
    [saveProgress]
  );

  const clearVideoControlsHideTimer = useCallback(() => {
    if (videoControlsHideTimeoutRef.current !== null) {
      window.clearTimeout(videoControlsHideTimeoutRef.current);
      videoControlsHideTimeoutRef.current = null;
    }
  }, []);

  const scheduleVideoControlsHide = useCallback(() => {
    clearVideoControlsHideTimer();

    if (!isVideoPlaying) {
      setAreVideoControlsVisible(true);
      return;
    }

    videoControlsHideTimeoutRef.current = window.setTimeout(() => {
      setAreVideoControlsVisible(false);
      videoControlsHideTimeoutRef.current = null;
    }, VIDEO_CONTROLS_HIDE_DELAY_MS);
  }, [clearVideoControlsHideTimer, isVideoPlaying]);

  const showVideoControls = useCallback(
    (options: { keepVisible?: boolean } = {}) => {
      setAreVideoControlsVisible(true);

      if (options.keepVisible || !isVideoPlaying) {
        clearVideoControlsHideTimer();
        return;
      }

      scheduleVideoControlsHide();
    },
    [clearVideoControlsHideTimer, isVideoPlaying, scheduleVideoControlsHide]
  );

  const hideVideoControls = useCallback(() => {
    clearVideoControlsHideTimer();

    if (isVideoPlaying) {
      setAreVideoControlsVisible(false);
    }
  }, [clearVideoControlsHideTimer, isVideoPlaying]);

  useEffect(() => {
    if (currentStatus !== "Video") {
      clearVideoControlsHideTimer();
      setAreVideoControlsVisible(true);
      return;
    }

    if (!isVideoPlaying) {
      clearVideoControlsHideTimer();
      setAreVideoControlsVisible(true);
      return;
    }

    scheduleVideoControlsHide();

    return () => {
      clearVideoControlsHideTimer();
    };
  }, [clearVideoControlsHideTimer, currentStatus, isVideoPlaying, scheduleVideoControlsHide]);

  useEffect(() => {
    const handleWindowBlur = () => {
      if (currentStatus === "Video" && isVideoPlaying) {
        hideVideoControls();
      }
    };

    window.addEventListener("blur", handleWindowBlur);

    return () => {
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [currentStatus, hideVideoControls, isVideoPlaying]);

  const handleVideoLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video || !session) {
      return;
    }

    setVideoDuration(video.duration || 0);

    const persistedCheckpoint = Math.max(0, Math.floor(session.videoCheckpointSeconds || 0));
    const completedProgress = Math.floor(video.duration || persistedCheckpoint);
    maxAllowedTimeRef.current = session.videoCompleted ? completedProgress : persistedCheckpoint;
    lastSavedCheckpointRef.current = persistedCheckpoint;
    setIsVideoMuted(video.muted);
    setIsVideoPlaying(false);
    clearVideoControlsHideTimer();
    setAreVideoControlsVisible(true);
    setSeekPreviewSeconds(null);

    if (session.videoCompleted) {
      isProgrammaticSeekRef.current = true;
      video.currentTime = 0;
      setCurrentPlaybackSeconds(0);
      setWatchedProgressSeconds(completedProgress);
      window.setTimeout(() => {
        isProgrammaticSeekRef.current = false;
      }, 0);
      return;
    }

    setWatchedProgressSeconds(persistedCheckpoint);
    setCurrentPlaybackSeconds(persistedCheckpoint);

    if (persistedCheckpoint > 0 && !session.videoCompleted) {
      const safeResumePoint = Math.min(
        persistedCheckpoint,
        Math.max(0, Math.floor((video.duration || persistedCheckpoint) - 1))
      );

      if (safeResumePoint > 0) {
        isProgrammaticSeekRef.current = true;
        video.currentTime = safeResumePoint;
        window.setTimeout(() => {
          isProgrammaticSeekRef.current = false;
        }, 0);
        setCurrentPlaybackSeconds(safeResumePoint);
      }
    }
  };

  const handleVideoTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || !session) {
      return;
    }

    const currentTime = Math.max(0, Math.floor(video.currentTime));
    if (seekPreviewSeconds === null) {
      setCurrentPlaybackSeconds(currentTime);
    }

    if (session.videoCompleted) {
      setWatchedProgressSeconds(Math.max(watchedProgressSeconds, Math.floor(videoDuration || currentTime)));
      return;
    }

    if (currentTime > maxAllowedTimeRef.current) {
      maxAllowedTimeRef.current = currentTime;
      setWatchedProgressSeconds(currentTime);
    }

    if (currentTime - lastSavedCheckpointRef.current >= 5) {
      lastSavedCheckpointRef.current = currentTime;
      void persistVideoCheckpoint(currentTime);
    }
  };

  const handleVideoPause = () => {
    const video = videoRef.current;
    if (!video || !session) {
      return;
    }

    setIsVideoPlaying(false);
    clearVideoControlsHideTimer();
    setAreVideoControlsVisible(true);
    setSeekPreviewSeconds(null);
    const currentTime = Math.max(0, Math.floor(video.currentTime));
    setCurrentPlaybackSeconds(currentTime);

    if (session.videoCompleted) {
      return;
    }

    if (currentTime > lastSavedCheckpointRef.current) {
      lastSavedCheckpointRef.current = currentTime;
      void persistVideoCheckpoint(currentTime);
    }
  };

  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
    setVideoError("");
    setAreVideoControlsVisible(true);
  };

  const handleVideoSeeking = () => {
    const video = videoRef.current;
    if (!video || !session || session.videoCompleted || isProgrammaticSeekRef.current) {
      return;
    }

    const allowedTime = maxAllowedTimeRef.current + 1;
    if (video.currentTime > allowedTime) {
      isProgrammaticSeekRef.current = true;
      video.currentTime = maxAllowedTimeRef.current;
      window.setTimeout(() => {
        isProgrammaticSeekRef.current = false;
      }, 0);
    }
  };

  const handleVideoEnded = async () => {
    const video = videoRef.current;
    if (!video || !session) {
      return;
    }

    const completedCheckpoint = Math.max(0, Math.floor(video.duration || video.currentTime));
    setCurrentPlaybackSeconds(completedCheckpoint);
    setIsVideoPlaying(false);
    clearVideoControlsHideTimer();
    setAreVideoControlsVisible(true);

    try {
      await saveProgress({
        videoCheckpointSeconds: completedCheckpoint,
        videoCompleted: true,
      });
      maxAllowedTimeRef.current = completedCheckpoint;
      lastSavedCheckpointRef.current = completedCheckpoint;
      setWatchedProgressSeconds(completedCheckpoint);
    } catch (error: any) {
      setVideoError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to save video completion."
      );
    }
  };

  const handleVideoPlayPause = async () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    setVideoError("");
    setAreVideoControlsVisible(true);

    try {
      if (video.paused || video.ended) {
        if (video.ended) {
          isProgrammaticSeekRef.current = true;
          video.currentTime = 0;
          setCurrentPlaybackSeconds(0);
          window.setTimeout(() => {
            isProgrammaticSeekRef.current = false;
          }, 0);
        }

        await video.play();
        setIsVideoPlaying(true);
      } else {
        video.pause();
        setIsVideoPlaying(false);
      }
    } catch (error) {
      setVideoError("Unable to control video playback right now. Please try again.");
    }
  };

  const handleVideoSliderChange = (_event: Event, value: number | number[]) => {
    showVideoControls({ keepVisible: true });
    const rawValue = Array.isArray(value) ? value[0] : value;
    const clampedValue = Math.min(Math.max(0, rawValue), seekableProgressSeconds);
    setSeekPreviewSeconds(clampedValue);
  };

  const handleVideoSliderCommit = (_event: Event | React.SyntheticEvent, value: number | number[]) => {
    const video = videoRef.current;
    if (!video) {
      setSeekPreviewSeconds(null);
      return;
    }

    const rawValue = Array.isArray(value) ? value[0] : value;
    const clampedValue = Math.min(Math.max(0, rawValue), seekableProgressSeconds);

    isProgrammaticSeekRef.current = true;
    video.currentTime = clampedValue;
    setCurrentPlaybackSeconds(clampedValue);
    setSeekPreviewSeconds(null);

    window.setTimeout(() => {
      isProgrammaticSeekRef.current = false;
    }, 0);

    showVideoControls({ keepVisible: video.paused || video.ended });
  };

  const handleVideoMuteToggle = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    showVideoControls();
    video.muted = !video.muted;
    setIsVideoMuted(video.muted);
  };

  const handleVideoFrameClick = () => {
    void handleVideoPlayPause();
  };

  const handleVideoFrameMouseLeave = () => {
    setSeekPreviewSeconds(null);
    hideVideoControls();
  };

  const handleVideoFrameBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const nextFocusedElement = event.relatedTarget;

    if (nextFocusedElement instanceof Node && event.currentTarget.contains(nextFocusedElement)) {
      return;
    }

    scheduleVideoControlsHide();
  };

  const handleVideoFullscreenToggle = async () => {
    const fullscreenDocument = document as FullscreenCapableDocument;
    const fullscreenElement =
      document.fullscreenElement || fullscreenDocument.webkitFullscreenElement || null;
    const target = videoFrameRef.current as FullscreenCapableElement | null;

    showVideoControls();

    try {
      if (fullscreenElement) {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (fullscreenDocument.webkitExitFullscreen) {
          await fullscreenDocument.webkitExitFullscreen();
        }
        return;
      }

      if (!target) {
        return;
      }

      if (target.requestFullscreen) {
        await target.requestFullscreen();
      } else if (target.webkitRequestFullscreen) {
        await target.webkitRequestFullscreen();
      }
    } catch (error) {
      setVideoError("Unable to toggle fullscreen right now. Please try again.");
    }
  };

  const handleVideoStepContinue = async () => {
    if (!session?.videoCompleted) {
      setVideoError("Please finish watching the full video before continuing.");
      return;
    }

    setVideoError("");
    await handleStatusTransition("Question 1");
  };

  const handleSignatureEnd = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      const nextSignatureData = getSignatureDataUrl(signaturePadRef.current, signatureData);
      setSignatureData(nextSignatureData);
      lastHydratedSignatureRef.current = nextSignatureData;

      if (isSignatureValid(signaturePadRef.current, nextSignatureData)) {
        setAttestationError("");
      }
    }
  };

  const handleClearSignature = () => {
    setSignatureData("");
    lastHydratedSignatureRef.current = "";
    setSignatureRefreshKey((previous) => previous + 1);
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  const handleQuestionFeedbackAction = async () => {
    if (!questionFeedback) {
      return;
    }

    if (questionFeedback.action === "retry") {
      if (!currentQuestion) {
        return;
      }

      try {
        await saveProgress({
          clearQuestionRetry: currentQuestion.status,
        });
        setSelectedAnswer("");
        setAnswerError("");
        setQuestionFeedback(null);
      } catch (error: any) {
        setPageMessage({
          severity: "error",
          text:
            error?.response?.data?.message ||
            error?.message ||
            "Unable to prepare the question for another try.",
        });
      }
      return;
    }

    if (questionFeedback.action === "advance" && questionFeedback.nextStatus) {
      setQuestionFeedback(null);
      await handleStatusTransition(questionFeedback.nextStatus);
      return;
    }

    if (questionFeedback.action === "restart") {
      await resetTrainingAfterFailedQuiz(questionFeedback.finalScore || 0);
    }
  };

  const handleQuestionSubmit = async () => {
    if (!session || !currentQuestion) {
      return;
    }

    if (!selectedAnswer) {
      setAnswerError("Please select an answer before continuing.");
      return;
    }

    try {
      const updatedTraining = await saveProgress({
        questionSubmission: {
          status: currentQuestion.status,
          answer: selectedAnswer,
        },
      });
      setAnswerError("");
      setQuestionFeedback(
        normalizeStepMetadata(updatedTraining?.stepMetadata).questionFeedbackByStatus?.[
          currentQuestion.status
        ] || null
      );
    } catch (error: any) {
      setPageMessage({
        severity: "error",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to save your answer right now.",
      });
    }
  };

  const handleAttestationSubmit = async () => {
    if (!isAttested) {
      setAttestationError("Please confirm the acknowledgement before finishing.");
      return;
    }

    const currentSignatureData = getSignatureDataUrl(signaturePadRef.current, signatureData);

    if (!isSignatureValid(signaturePadRef.current, currentSignatureData)) {
      setAttestationError("Please provide a valid signature before finishing.");
      return;
    }

    try {
      setSignatureData(currentSignatureData);
      await saveProgress({
        completeAttestation: true,
        signature: currentSignatureData,
      });
      setCompletedViewMode("fresh");
      setAccessState("completed");
      setPageMessage({
        severity: "success",
        text: "HIPAA training completed successfully.",
      });
    } catch (error: any) {
      setPageMessage({
        severity: "error",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to finish the HIPAA training right now.",
      });
    }
  };

  const handleCertificateDownload = async () => {
    if (!session) return;
    setIsDownloadingCertificate(true);
    setCertificateError("");
    try {
      const response = await fetch(`${API_BASE_URL}/hipaa-training/certificate/${session.id}`, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.message || "Unable to download the training certificate.");
      }
      const disposition = response.headers.get("Content-Disposition") || "";
      const filename = /filename="([^"]+)"/.exec(disposition)?.[1] || "HIPAA-Training-Certificate.pdf";
      const url = URL.createObjectURL(await response.blob());
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      setCertificateError(error?.message || "Unable to download the training certificate.");
    } finally {
      setIsDownloadingCertificate(false);
    }
  };

  const renderHeroImage = (src: string, alt: string) => (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%", mb: 4 }}>
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: "100%",
          maxWidth: 700,
          display: "block",
          borderRadius: 2,
          boxShadow: 3,
        }}
      />
    </Box>
  );

  const renderInvalidState = () => (
    <Container maxWidth="sm" sx={{ mt: 6, mb: 6 }}>
      <Paper elevation={6} sx={{ p: { xs: 3, sm: 5 }, textAlign: "center", borderRadius: 3 }}>
        <CancelOutlinedIcon sx={{ fontSize: 64, color: "#8900e1", mb: 2 }} />
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Unable to Access Training
        </Typography>
        <Typography color="text.secondary">
          {pageMessage?.text || "This training link is not valid."}
        </Typography>
      </Paper>
    </Container>
  );

  const renderCompletedState = (alreadyCompleted: boolean) => (
    <Container maxWidth="md" sx={{ mt: 5, mb: 6 }}>
      <Paper elevation={6} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
        <Stack spacing={3} alignItems="center" textAlign="center">
          <CheckCircleOutlineIcon sx={{ fontSize: 64, color: "#8900e1" }} />
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {alreadyCompleted ? "Training Already Completed" : HIPAA_TRAINING_COPY.completed.title}
          </Typography>
          {renderHeroImage(thankYouImageUrl, "HIPAA training completed")}
          <Typography color="text.secondary" sx={{ maxWidth: 620 }}>
            {alreadyCompleted
              ? "This HIPAA training has already been completed for this user."
              : HIPAA_TRAINING_COPY.completed.body}
          </Typography>
          {session?.completedAt ? (
            <Typography variant="body2" color="text.secondary">
              Completed at {formatDateTime(session.completedAt)}
            </Typography>
          ) : null}
          {certificateError ? (
            <Alert severity="error" sx={{ width: "100%", maxWidth: 560 }}>
              {certificateError}
            </Alert>
          ) : null}
          <Button
            variant="contained"
            startIcon={<DownloadOutlinedIcon />}
            onClick={() => void handleCertificateDownload()}
            disabled={isDownloadingCertificate}
          >
            {isDownloadingCertificate ? "Preparing Certificate..." : "Download Certificate"}
          </Button>
        </Stack>
      </Paper>
    </Container>
  );

  const renderVerificationState = () => (
    <Container maxWidth="sm" sx={{ mt: 6, mb: 6 }}>
      <Paper elevation={6} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
        <Stack
          spacing={3}
          component="form"
          onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            void handleVerifyEmail();
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {isEmailMode && emailChallengeToken
                ? "Check Your Email"
                : isEmailMode
                  ? "Enter Your Email"
                  : "Verify Your Email"}
            </Typography>
            <Typography color="text.secondary">
              {isEmailMode && emailChallengeToken
                ? "Enter the 6-digit code we sent to your email address."
                : isEmailMode
                ? "Enter your email address to start or resume this HIPAA training."
                : "Enter the email address on your account to continue this HIPAA training."}
            </Typography>
            {verificationEmailHint ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Expected email: {verificationEmailHint}
              </Typography>
            ) : null}
          </Box>

          {isEmailMode ? (
            <TextField
              label="Full name"
              value={verificationName}
              onChange={(event) => {
                setVerificationName(event.target.value);
                setVerificationError("");
                setVerificationMessage("");
                setVerificationCode("");
                setEmailChallengeToken("");
              }}
              fullWidth
              required
              autoComplete="name"
              autoFocus
              inputProps={{ maxLength: 120 }}
              helperText="This name will appear on your certificate of completion."
            />
          ) : null}

          <TextField
            label="Email address"
            type="email"
            value={verificationEmail}
            onChange={(event) => {
              setVerificationEmail(event.target.value);
              setVerificationError("");
              setVerificationMessage("");
              setVerificationCode("");
              setEmailChallengeToken("");
            }}
            fullWidth
            autoComplete="email"
            autoFocus={!isEmailMode}
          />

          {isEmailMode && emailChallengeToken ? (
            <TextField
              label="Verification code"
              value={verificationCode}
              onChange={(event) => {
                setVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 6));
                setVerificationError("");
              }}
              fullWidth
              required
              autoFocus
              autoComplete="one-time-code"
              inputProps={{ inputMode: "numeric", pattern: "[0-9]{6}", maxLength: 6 }}
            />
          ) : null}

          {verificationMessage ? <Alert severity="info">{verificationMessage}</Alert> : null}

          {verificationError ? <Alert severity="error">{verificationError}</Alert> : null}

          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
            {isEmailMode && emailChallengeToken ? (
              <Button
                type="button"
                onClick={() => {
                  setEmailChallengeToken("");
                  setVerificationCode("");
                  setVerificationMessage("");
                  setVerificationError("");
                }}
              >
                Send a new code
              </Button>
            ) : <span />}
            <Button
              variant="contained"
              type="submit"
              disabled={isVerifyingEmail}
            >
              {isVerifyingEmail
                ? isEmailMode
                  ? emailChallengeToken
                    ? "Verifying..."
                    : "Sending..."
                  : "Verifying..."
                : isEmailMode
                  ? emailChallengeToken
                    ? "Verify Code"
                    : "Send Code"
                  : "Verify Email"}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Container>
  );

  if (isInitialLoading) {
    return (
      <Backdrop
        open
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (accessState === "verification_required" || accessState === "email_required") {
    return renderVerificationState();
  }

  if (accessState === "invalid" || !session) {
    return renderInvalidState();
  }

  if (accessState === "completed") {
    return renderCompletedState(completedViewMode === "existing");
  }

  return (
    <Container maxWidth={false} disableGutters sx={{ mt: 4, mb: 6 }}>
      <Backdrop
        open={isSaving}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Paper
        elevation={6}
        sx={{
          p: { xs: 3, sm: 4, md: 5 },
          borderRadius: 3,
          width: "100%",
          maxWidth: 1080,
          mx: "auto",
        }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              HIPAA Training
            </Typography>
            <Typography color="text.secondary">
              {displayName ? `Assigned to ${displayName}` : "Assigned training"}
            </Typography>
          </Box>

          <Box sx={{ width: "100%", mt: 1 }}>
            <NavStatusBar
              steps={statusBarOptions.map((option) => ({
                status: option.value,
                title: option.label,
                disabled: option.disabled,
              }))}
              currentStep={currentStepIndex}
              onStepClick={(stepIndex) => {
                const option = statusBarOptions[stepIndex];
                if (option && !option.disabled && option.value !== currentStatus) {
                  void handleStatusTransition(option.value as HipaaTrainingStatus);
                }
              }}
              disabled={isSaving}
            />
          </Box>

          <Box aria-hidden="true" sx={{ height: { xs: 3, sm: 5, md: 6 } }} />

          {pageMessage ? <Alert severity={pageMessage.severity}>{pageMessage.text}</Alert> : null}

          {currentStatus === "Overview" ? (
            <>
              {renderHeroImage(overviewImageUrl, "HIPAA overview")}
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {HIPAA_TRAINING_COPY.overview.title}
              </Typography>
              <Stack spacing={2}>
                {HIPAA_TRAINING_COPY.overview.body.map((paragraph) => (
                  <Typography key={paragraph}>{paragraph}</Typography>
                ))}
              </Stack>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button variant="contained" onClick={() => void handleStatusTransition("Why HIPAA Matters")}>
                  Next
                </Button>
              </Box>
            </>
          ) : null}

          {currentStatus === "Why HIPAA Matters" ? (
            <>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {HIPAA_TRAINING_COPY.whyHipaaMatters.title}
              </Typography>
              <Stack spacing={2}>
                {HIPAA_TRAINING_COPY.whyHipaaMatters.body.map((paragraph) => (
                  <Typography key={paragraph}>{paragraph}</Typography>
                ))}
              </Stack>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button variant="outlined" onClick={() => void handleStatusTransition("Overview")}>
                  Back
                </Button>
                <Button variant="contained" onClick={() => void handleStatusTransition("What To Expect")}>
                  Next
                </Button>
              </Box>
            </>
          ) : null}

          {currentStatus === "What To Expect" ? (
            <>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {HIPAA_TRAINING_COPY.whatToExpect.title}
              </Typography>
              <Stack component="ul" spacing={1} sx={{ pl: 3, m: 0 }}>
                {HIPAA_TRAINING_COPY.whatToExpect.bullets.map((bullet) => (
                  <Typography component="li" key={bullet}>
                    {bullet}
                  </Typography>
                ))}
              </Stack>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button variant="outlined" onClick={() => void handleStatusTransition("Why HIPAA Matters")}>
                  Back
                </Button>
                <Button variant="contained" onClick={() => void handleStatusTransition("Video")}>
                  Start Video
                </Button>
              </Box>
            </>
          ) : null}

          {currentStatus === "Video" ? (
            <>
              <Stack direction="row" spacing={1} alignItems="center">
                <PlayCircleOutlineIcon color="action" />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  Watch the Video
                </Typography>
              </Stack>
              <Typography color="text.secondary">
                Please watch the full video before continuing to the quiz.
              </Typography>
              <Card variant="outlined" sx={{ overflow: "hidden", borderRadius: 3 }}>
                <CardContent sx={{ p: { xs: 1.25, sm: 2 }, "&:last-child": { pb: { xs: 1.25, sm: 2 } } }}>
                  <Box
                    ref={videoFrameRef}
                    sx={{
                      position: "relative",
                      width: "100%",
                      aspectRatio: "16 / 9",
                      backgroundColor: "#000",
                      borderRadius: 2,
                      overflow: "hidden",
                      cursor: isVideoPlaying && !shouldShowVideoControls ? "none" : "pointer",
                    }}
                    onClick={handleVideoFrameClick}
                    onMouseMove={() => showVideoControls()}
                    onMouseEnter={() => showVideoControls({ keepVisible: !isVideoPlaying })}
                    onMouseLeave={handleVideoFrameMouseLeave}
                    onTouchStart={() => showVideoControls()}
                    onFocusCapture={() => showVideoControls({ keepVisible: true })}
                    onBlurCapture={handleVideoFrameBlur}
                  >
                    <Box
                      component="video"
                      ref={videoRef}
                      src={trainingVideoUrl}
                      preload="metadata"
                      playsInline
                      controls={false}
                      disablePictureInPicture
                      controlsList="nodownload noplaybackrate noremoteplayback"
                      tabIndex={-1}
                      onLoadedMetadata={handleVideoLoadedMetadata}
                      onTimeUpdate={handleVideoTimeUpdate}
                      onPause={handleVideoPause}
                      onPlay={handleVideoPlay}
                      onVolumeChange={() => setIsVideoMuted(videoRef.current?.muted ?? false)}
                      onSeeking={handleVideoSeeking}
                      onEnded={() => void handleVideoEnded()}
                      onContextMenu={(event: React.MouseEvent<HTMLVideoElement>) => event.preventDefault()}
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "block",
                        backgroundColor: "#000",
                      }}
                    />
                    {!isVideoPlaying ? (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background:
                            "linear-gradient(to bottom, rgba(0,0,0,0.18), rgba(0,0,0,0.34))",
                          pointerEvents: "none",
                        }}
                      >
                        <Box
                          sx={{
                            width: 74,
                            height: 74,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(137, 0, 225, 0.88)",
                            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.35)",
                          }}
                        >
                          <PlayArrowIcon sx={{ color: "#fff", fontSize: 42 }} />
                        </Box>
                      </Box>
                    ) : null}
                    <Box
                      sx={{
                        position: "absolute",
                        insetInline: 0,
                        bottom: 0,
                        px: { xs: 1, sm: 1.5 },
                        py: { xs: 1, sm: 1.25 },
                        background:
                          "linear-gradient(to top, rgba(0,0,0,0.82), rgba(0,0,0,0.48) 58%, rgba(0,0,0,0))",
                        opacity: shouldShowVideoControls ? 1 : 0,
                        transform: shouldShowVideoControls ? "translateY(0)" : "translateY(10px)",
                        pointerEvents: shouldShowVideoControls ? "auto" : "none",
                        transition: "opacity 180ms ease, transform 180ms ease",
                      }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Stack spacing={1}>
                        <Box sx={{ position: "relative", px: 0.25 }}>
                          <Box
                            sx={{
                              position: "absolute",
                              insetInline: 0,
                              top: "50%",
                              transform: "translateY(-50%)",
                              height: 6,
                              borderRadius: 999,
                              backgroundColor: "rgba(255,255,255,0.22)",
                            }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              left: 0,
                              top: "50%",
                              transform: "translateY(-50%)",
                              height: 6,
                              borderRadius: 999,
                              width: `${seekableProgressPercent}%`,
                              backgroundColor: "rgba(255,255,255,0.38)",
                            }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              left: 0,
                              top: "50%",
                              transform: "translateY(-50%)",
                              height: 6,
                              borderRadius: 999,
                              width: `${displayedSeekPercent}%`,
                              backgroundColor: "#ffffff",
                            }}
                          />
                          <Slider
                            min={0}
                            max={Math.max(videoDuration, 1)}
                            step={1}
                            value={Math.min(displayedSeekSeconds, Math.max(videoDuration, 1))}
                            onChange={handleVideoSliderChange}
                            onChangeCommitted={handleVideoSliderCommit}
                            aria-label="Video progress"
                            sx={{
                              color: "#ffffff",
                              py: 0,
                              height: 14,
                              display: "flex",
                              alignItems: "center",
                              "& .MuiSlider-rail": {
                                opacity: 0,
                              },
                              "& .MuiSlider-track": {
                                opacity: 0,
                              },
                              "& .MuiSlider-thumb, & .MuiSlider-markLabel": {
                                top: "50%",
                              },
                              "& .MuiSlider-thumb": {
                                width: 14,
                                height: 14,
                                backgroundColor: "#ffffff",
                                boxShadow: "0 0 0 4px rgba(137,0,225,0.22)",
                                transform: "translate(-50%, -50%)",
                              },
                            }}
                          />
                        </Box>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <IconButton
                              aria-label={isVideoPlaying ? "Pause video" : "Play video"}
                              onClick={() => void handleVideoPlayPause()}
                              sx={{ color: "#fff" }}
                            >
                              {isVideoPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                            </IconButton>
                            <Typography variant="body2" sx={{ color: "#fff", minWidth: 108 }}>
                              {formatDuration(displayedSeekSeconds)} / {formatDuration(videoDuration)}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <IconButton
                              aria-label={isVideoMuted ? "Unmute video" : "Mute video"}
                              onClick={handleVideoMuteToggle}
                              sx={{ color: "#fff" }}
                            >
                              {isVideoMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                            </IconButton>
                            <IconButton
                              aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                              onClick={() => void handleVideoFullscreenToggle()}
                              sx={{ color: "#fff" }}
                            >
                              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                            </IconButton>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2.5}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }} />
              </Stack>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    The quiz will unlock after the full video has been watched.
                  </Typography>
                  {session.videoCompleted ? (
                    <Typography variant="body2" color="text.secondary">
                      Video requirement complete. You may continue to the quiz.
                    </Typography>
                  ) : null}
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button variant="outlined" onClick={() => void handleStatusTransition("What To Expect")}>
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!session.videoCompleted}
                    onClick={() => void handleVideoStepContinue()}
                  >
                    Continue to Quiz
                  </Button>
                </Stack>
              </Stack>
              {videoError ? <Alert severity="warning">{videoError}</Alert> : null}
            </>
          ) : null}

          {currentQuestion ? (
            <>
              {currentQuestion.status === "Question 1" ? (
                <>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <QuizOutlinedIcon color="action" />
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {HIPAA_TRAINING_COPY.knowledgeCheck.title}
                    </Typography>
                  </Stack>
                  <Typography>{HIPAA_TRAINING_COPY.knowledgeCheck.body}</Typography>
                  <Typography color="text.secondary">
                    {HIPAA_TRAINING_COPY.knowledgeCheck.note}
                  </Typography>
                </>
              ) : null}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <Typography variant="overline" color="text.secondary">
                  Retries remaining: {retriesRemaining}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentQuestion.status}
                </Typography>
              </Stack>
              <FormControl
                fullWidth
                error={Boolean(answerError)}
                disabled={isQuestionLocked}
              >
                <FormLabel sx={{ mb: 2, color: "text.primary", fontWeight: 600 }}>
                  {currentQuestion.prompt}
                </FormLabel>
                <RadioGroup
                  value={displayedSelectedAnswer}
                  onChange={(event) => {
                    const nextValue = event.target.value as HipaaAnswerOption;
                    if (isQuestionLocked || retryDisabledAnswers.includes(nextValue)) {
                      return;
                    }
                    setSelectedAnswer(nextValue);
                    setAnswerError("");
                  }}
                >
                  {currentQuestion.options.map((option) => (
                    (() => {
                      const isSelected = displayedSelectedAnswer === option.value;
                      const isIncorrectAnswer = incorrectAnswers.includes(option.value);
                      const isCorrectAnswer =
                        questionFeedback?.answer === option.value &&
                        questionFeedback?.isCorrect === true;
                      const feedbackColor = isCorrectAnswer ? "success.main" : "error.main";
                      const isRetryLocked =
                        questionFeedback?.action === "retry" && isIncorrectAnswer;

                      return (
                        <Paper
                          key={option.value}
                          variant="outlined"
                          sx={{
                            mb: 1.5,
                            px: 2,
                            py: 1,
                            opacity: isRetryLocked ? 0.88 : 1,
                            borderColor: isCorrectAnswer || isIncorrectAnswer
                              ? feedbackColor
                              : isSelected
                                ? "#8900e1"
                                : undefined,
                            backgroundColor: isCorrectAnswer || isIncorrectAnswer
                              ? isCorrectAnswer
                                ? "rgba(46, 125, 50, 0.08)"
                                : "rgba(211, 47, 47, 0.06)"
                              : undefined,
                          }}
                        >
                          <FormControlLabel
                            value={option.value}
                            control={
                              <Radio
                                sx={{ color: "#8900e1", "&.Mui-checked": { color: "#8900e1" } }}
                              />
                            }
                            disabled={isRetryLocked || isQuestionLocked}
                            label={
                              <Box>
                                <Typography component="span">
                                  {`${option.value}. ${option.label}`}
                                </Typography>
                                {isCorrectAnswer || isIncorrectAnswer ? (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "block",
                                      mt: 0.5,
                                      color: feedbackColor,
                                      fontWeight: 600,
                                    }}
                                  >
                                    {isCorrectAnswer
                                      ? "Correct answer"
                                      : "Incorrect answer"}
                                  </Typography>
                                ) : null}
                              </Box>
                            }
                            sx={{ alignItems: "center", m: 0, width: "100%" }}
                          />
                        </Paper>
                      );
                    })()
                  ))}
                </RadioGroup>
              </FormControl>
              {questionFeedback ? (
                <Alert severity={questionFeedback.severity}>{questionFeedback.text}</Alert>
              ) : null}
              {answerError ? <Alert severity="warning">{answerError}</Alert> : null}
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Button
                  variant="outlined"
                  disabled={!previousStatus}
                  onClick={() => {
                    if (previousStatus) {
                      void handleStatusTransition(previousStatus);
                    }
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={() =>
                    void (
                      questionFeedback && questionFeedback.action !== "retry"
                        ? handleQuestionFeedbackAction()
                        : handleQuestionSubmit()
                    )
                  }
                >
                  {questionFeedback?.action === "advance"
                      ? "Continue"
                      : questionFeedback?.action === "restart"
                        ? "Restart Training"
                        : "Submit Answer"}
                </Button>
              </Box>
            </>
          ) : null}

          {currentStatus === "Attestation" ? (
            <>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {HIPAA_TRAINING_COPY.attestation.title}
              </Typography>
              <Typography>{HIPAA_TRAINING_COPY.attestation.intro}</Typography>
              <Stack component="ul" spacing={1} sx={{ pl: 3, m: 0 }}>
                {HIPAA_TRAINING_COPY.attestation.bullets.map((bullet) => (
                  <Typography component="li" key={bullet}>
                    {bullet}
                  </Typography>
                ))}
              </Stack>
              <Divider />
              <Box>
                <Typography sx={{ fontWeight: 600, mb: 1.5 }}>
                  Signature
                </Typography>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: attestationError ? "error.main" : "divider",
                    borderRadius: 2,
                    backgroundColor: "#fff",
                    height: 180,
                    overflow: "hidden",
                  }}
                >
                  <SignaturePad
                    key={signatureRefreshKey}
                    ref={signaturePadRef}
                    onEnd={handleSignatureEnd}
                    canvasProps={{
                      style: { width: "100%", height: "180px", display: "block" },
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                  <Button variant="text" onClick={handleClearSignature}>
                    Clear Signature
                  </Button>
                </Box>
              </Box>
              <FormControl error={Boolean(attestationError)}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isAttested}
                      onChange={(event) => setIsAttested(event.target.checked)}
                    />
                  }
                  label={HIPAA_TRAINING_COPY.attestation.checkboxLabel}
                />
              </FormControl>
              {attestationError ? <Alert severity="warning">{attestationError}</Alert> : null}
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button variant="outlined" onClick={() => void handleStatusTransition("Question 5")}>
                  Back
                </Button>
                <Button variant="contained" onClick={() => void handleAttestationSubmit()}>
                  Finish Training
                </Button>
              </Box>
            </>
          ) : null}

          {showScoreFooter ? (
            <>
              <Divider />
              <Typography variant="body2" color="text.secondary">
                Current score: {session.correctAnswerCount} / 5
                {session.score >= 0
                  ? ` (${Math.round(session.score * 100)}%)`
                  : ""}
              </Typography>
            </>
          ) : null}
        </Stack>
      </Paper>
    </Container>
  );
}
